import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight, ArrowUpRight, Quote, Compass, Heart, Sparkles,
    ShieldCheck, Leaf, Users, Hammer, Target, MessageCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import CtaBanner from "@/components/CtaBanner";
import InlineCTA from "@/components/InlineCTA";
import { usePageContent, useContentList, useSetting } from "@/context/CmsContext";
import { useLocale } from "@/context/LocaleContext";
import useSeo from "@/lib/useSeo";
import { resolveIcon } from "@/lib/cmsHelpers";

const HERO_IMG_DEFAULT = "https://images.pexels.com/photos/9512065/pexels-photo-9512065.jpeg?auto=compress&cs=tinysrgb&w=1400&q=80";
const STORY_IMG_DEFAULT = "https://images.unsplash.com/photo-1759142761123-9ab45592b5f6?w=1400&q=80";
const TEAM_IMG_DEFAULT = "https://images.unsplash.com/photo-1768315010476-fabee11fb87a?w=1400&q=80";

const VALUES_FALLBACK = [
    { icon: "Leaf", title: "Sustentabilidade", desc: "Cada NOVIFOOD reduz a dependência de combustíveis fósseis. Painéis solares, baterias de lítio e materiais recicláveis são a base do nosso design." },
    { icon: "Hammer", title: "Engenharia premium", desc: "Construímos cada unidade em alumínio reforçado, com componentes certificados e acabamentos pensados para durar anos em operação intensiva." },
    { icon: "Heart", title: "Próximos do cliente", desc: "Acompanhamos cada empreendedor da configuração à primeira venda. Não vendemos carrinhos — entregamos um negócio pronto a operar." },
];

const TIMELINE_FALLBACK = [
    { year: "2019", title: "Os primeiros protótipos", desc: "Iniciámos com testes de campo em feiras urbanas no Porto." },
    { year: "2021", title: "Lançamento do Modelo Alfa", desc: "Apresentámos a primeira ilha funcional 100% solar." },
    { year: "2023", title: "Modelo Ômega & Marcas", desc: "Estendemos a oferta a operações de maior volume." },
    { year: "Hoje", title: "+300 carrinhos em rua", desc: "Estamos presentes em 15 cidades portuguesas." },
];

const VALUES_EN = [
    { icon: "Leaf", title: "Sustainability", desc: "Every NOVIFOOD reduces fossil-fuel dependency. Solar panels, lithium batteries and recyclable materials are the basis of our design." },
    { icon: "Hammer", title: "Premium engineering", desc: "We build every unit in reinforced aluminium, with certified components and finishes designed to last years of intensive operation." },
    { icon: "Heart", title: "Close to the client", desc: "We support every entrepreneur from configuration to first sale. We don't sell carts — we deliver a business ready to operate." },
];

const TIMELINE_EN = [
    { year: "2019", title: "The first prototypes", desc: "We started with field tests at urban fairs in Porto." },
    { year: "2021", title: "Alfa Model launch", desc: "We introduced the first 100% solar functional island." },
    { year: "2023", title: "Omega Model & Brands", desc: "We extended the offer to higher-volume operations." },
    { year: "Today", title: "300+ carts on the street", desc: "We are present in 15 Portuguese cities." },
];

export default function Sobre() {
    const [testimonials, setTestimonials] = useState([]);
    const [loading, setLoading] = useState(true);
    const { t } = usePageContent("sobre");
    const { tr, en } = useLocale();
    const cmsValues = useContentList("sobre_values", VALUES_FALLBACK, { forceFallbackEn: false });
    const cmsTimeline = useContentList("sobre_timeline", TIMELINE_FALLBACK, { forceFallbackEn: false });
    const values = en ? VALUES_EN : cmsValues;
    const timeline = en ? TIMELINE_EN : cmsTimeline;
    const whatsappNumber = useSetting("whatsapp_number", "351900000000");

    const HERO_IMG = t("hero", "image", HERO_IMG_DEFAULT);
    const STORY_IMG = t("story", "image", STORY_IMG_DEFAULT);
    const TEAM_IMG = t("team", "image", TEAM_IMG_DEFAULT);

    useSeo({
        title: tr("Sobre — A história da NOVIFOOD", "About — The NOVIFOOD story"),
        description: tr(
            "Fundada em Portugal, a NOVIFOOD desenha e produz carrinhos de food cart premium. Conheça a nossa missão, valores, equipa e os clientes que confiam em nós.",
            "Founded in Portugal, NOVIFOOD designs and builds premium food carts. Meet our mission, values, team and the clients who trust us."
        ),
        path: "/sobre",
    });

    useEffect(() => {
        let alive = true;
        api.get("/testimonials")
            .then((r) => alive && setTestimonials(r.data || []))
            .catch(() => alive && setTestimonials([]))
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, []);

    return (
        <>
            {/* HERO */}
            <section className="relative overflow-hidden border-b border-border gradient-navy" data-testid="sobre-hero">
                <div className="absolute inset-0 grain-overlay opacity-40 pointer-events-none" />
                <div
                    aria-hidden
                    className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full opacity-25 pointer-events-none"
                    style={{ background: "radial-gradient(circle, #FBBF24 0%, transparent 60%)" }}
                />
                <div className="container-x relative hero-y text-white">
                    <span className="inline-flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.28em] text-secondary">
                        <span aria-hidden className="inline-block h-[1.5px] w-6 bg-secondary" />
                        {t("hero", "eyebrow", tr("Sobre nós", "About us"))}
                    </span>
                    <h1 className="mt-3 display-xl max-w-4xl">
                        {(() => {
                            const raw = t("hero", "title", tr("Construímos os negócios móveis de uma {hl} geração.", "We build the mobile businesses of a {hl} generation."));
                            const hl = t("hero", "title_highlight", tr("nova", "new"));
                            if (hl && raw.includes("{hl}")) {
                                const parts = raw.split("{hl}");
                                return <>{parts[0]}<span className="serif-accent text-secondary">{hl}</span>{parts.slice(1).join("{hl}")}</>;
                            }
                            if (hl && raw.includes(hl)) {
                                const parts = raw.split(hl);
                                return <>{parts[0]}<span className="serif-accent text-secondary">{hl}</span>{parts.slice(1).join(hl)}</>;
                            }
                            return raw;
                        })()}
                    </h1>
                    <p className="mt-3 max-w-2xl text-sm sm:text-base text-white/80 leading-relaxed">
                        {t("hero", "subtitle", tr(
                            "A NOVIFOOD nasceu da convicção de que abrir um negócio próprio devia ser simples, sustentável e bonito.",
                            "NOVIFOOD was born from the conviction that opening your own business should be simple, sustainable and beautiful."
                        ))}
                    </p>
                    <div className="mt-5">
                        <Link to={t("hero", "cta_primary_to", "/contacto")} data-testid="sobre-cta-fale" className="btn-premium-gold">
                            {t("hero", "cta_primary_label", tr("Fale com a equipa", "Talk to the team"))} <ArrowRight className="h-4 w-4 arrow-shift" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* HISTÓRIA */}
            <section className="container-x section-y" data-testid="sobre-historia">
                <div className="grid lg:grid-cols-12 gap-8 sm:gap-12 items-center">
                    <div className="lg:col-span-6 relative">
                        <div className="aspect-[4/5] overflow-hidden rounded-2xl border border-border">
                            <img src={STORY_IMG} alt="Oficina NOVIFOOD" className="h-full w-full object-cover" />
                        </div>
                        <div className="absolute -bottom-6 -right-6 hidden md:flex items-center gap-3 rounded-xl bg-white border border-border px-5 py-4 shadow-lg">
                            <Compass className="h-5 w-5 text-secondary" />
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">Made in</p>
                                <p className="font-heading text-base font-bold text-primary">Portugal</p>
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-6">
                        <span className="eyebrow">{t("story", "eyebrow", tr("A nossa história", "Our story"))}</span>
                        <h2 className="mt-3 display-lg text-primary">
                            {t("story", "title", tr("De um protótipo de garagem a +300 carrinhos em rua.", "From a garage prototype to 300+ carts on the street."))}
                        </h2>
                        <p className="mt-6 text-muted-foreground leading-relaxed whitespace-pre-line">
                            {t("story", "body", tr("Começámos em 2019 numa pequena oficina no Porto...", "We started in 2019 in a small workshop in Porto..."))}
                        </p>
                    </div>
                </div>

                {/* TIMELINE */}
                <div className="mt-20 grid gap-6 md:grid-cols-2 lg:grid-cols-4" data-testid="sobre-timeline">
                    {timeline.map((it, i) => (
                        <div key={`${it.year}-${i}`} data-testid={`timeline-${i}`} className="rounded-2xl border border-border bg-white p-7 transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-[0_24px_48px_-16px_rgba(11,37,69,0.18)] hover:border-secondary/30">
                            <div className="font-heading text-3xl font-extrabold text-secondary">{it.year}</div>
                            <h3 className="mt-3 font-heading text-lg font-bold text-primary">{it.title}</h3>
                            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{it.desc}</p>
                        </div>
                    ))}
                </div>

                {/* Inline CTA — jornada → processos */}
                <div className="mt-14">
                    <InlineCTA
                        eyebrow={tr("A jornada continua", "The journey continues")}
                        title={tr("Descobre como transformamos o teu briefing em carrinho pronto.", "See how we turn your briefing into a ready-to-roll cart.")}
                        to="/processos"
                        label={tr("Ver processos", "See process")}
                        variant="gold"
                        icon={Sparkles}
                        testId="sobre-inline-cta-processos"
                    />
                </div>
            </section>

            {/* MISSÃO */}
            <section className="bg-primary text-white" data-testid="sobre-missao">
                <div className="container-x section-y">
                    <div className="grid lg:grid-cols-12 gap-8 sm:gap-12">
                        <div className="lg:col-span-5">
                            <span className="eyebrow text-secondary">{t("mission", "eyebrow", tr("Missão", "Mission"))}</span>
                            <h2 className="mt-3 display-lg">
                                {t("mission", "title", tr("Tornar o empreendedorismo móvel acessível.", "Make mobile entrepreneurship accessible."))}
                            </h2>
                            <p className="mt-6 text-white/75 text-lg leading-relaxed whitespace-pre-line">
                                {t("mission", "body", tr(
                                    "Acreditamos numa economia urbana onde qualquer pessoa pode abrir o seu negócio em poucas semanas.",
                                    "We believe in an urban economy where anyone can open their business in a few weeks."
                                ))}
                            </p>
                        </div>
                        <div className="lg:col-span-7 grid gap-5 sm:grid-cols-3">
                            <MissionTile icon={Target} title={t("mission", "tile1_title", tr("Acessibilidade", "Affordability"))} desc={t("mission", "tile1_desc", tr("Soluções a partir de €2.000, com financiamento até 48 meses.", "Solutions from €2,000, with financing up to 48 months."))} />
                            <MissionTile icon={ShieldCheck} title={t("mission", "tile2_title", tr("Confiança", "Trust"))} desc={t("mission", "tile2_desc", tr("2 anos de garantia estrutural e suporte técnico em 24h.", "2-year structural warranty and 24h technical support."))} />
                            <MissionTile icon={Users} title={t("mission", "tile3_title", tr("Comunidade", "Community"))} desc={t("mission", "tile3_desc", tr("Mais de 300 empreendedores activos e crescendo todos os meses.", "300+ active entrepreneurs, growing every month."))} />
                        </div>
                    </div>
                </div>
            </section>

            {/* VALORES */}
            <section className="container-x section-y" data-testid="sobre-valores">
                <div className="max-w-2xl">
                    <span className="eyebrow">{t("values_intro", "eyebrow", tr("Os nossos valores", "Our values"))}</span>
                    <h2 className="mt-3 display-lg text-primary">
                        {t("values_intro", "title", tr("Princípios que guiam cada parafuso.", "Principles guiding every bolt."))}
                    </h2>
                </div>
                <div className="mt-14 grid gap-6 md:grid-cols-3">
                    {values.map((v, i) => {
                        const Icon = resolveIcon(v.icon, "Sparkles");
                        return (
                            <div key={`${v.title}-${i}`} data-testid={`valor-${(v.title || "").toLowerCase().replace(/\s+/g, "-")}`}
                                className="group rounded-2xl border border-border bg-white p-7 sm:p-8 transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-[0_24px_48px_-16px_rgba(11,37,69,0.18)] hover:border-secondary/30 hover:border-secondary">
                                <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-muted text-primary group-hover:bg-secondary group-hover:text-primary transition">
                                    <Icon className="h-6 w-6" />
                                </div>
                                <h3 className="mt-5 font-heading text-2xl font-bold text-primary">{v.title}</h3>
                                <p className="mt-2 text-muted-foreground leading-relaxed">{v.desc}</p>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* EQUIPA / COMO TRABALHAMOS */}
            <section className="bg-muted border-y border-border" data-testid="sobre-equipa">
                <div className="container-x section-y">
                    <div className="grid lg:grid-cols-12 gap-8 sm:gap-12 items-center">
                        <div className="lg:col-span-6 order-2 lg:order-1">
                            <span className="eyebrow">{t("team", "eyebrow", tr("A equipa", "The team"))}</span>
                            <h2 className="mt-3 display-lg text-primary">
                                {t("team", "title", tr("Engenheiros, designers e operadores no mesmo lado do balcão.", "Engineers, designers and operators on the same side of the counter."))}
                            </h2>
                            <p className="mt-6 text-muted-foreground leading-relaxed whitespace-pre-line">
                                {t("team", "body", tr("Somos uma equipa pequena por opção.", "We are a small team by choice."))}
                            </p>
                            <div className="mt-8 grid grid-cols-3 gap-4">
                                <Stat label={t("team", "stat1_label", tr("Engenheiros", "Engineers"))} value={t("team", "stat1_value", "6")} />
                                <Stat label={t("team", "stat2_label", tr("Anos no mercado", "Years in market"))} value={t("team", "stat2_value", "6+")} />
                                <Stat label={t("team", "stat3_label", tr("Países servidos", "Countries served"))} value={t("team", "stat3_value", "4")} />
                            </div>
                        </div>
                        <div className="lg:col-span-6 order-1 lg:order-2">
                            <div className="aspect-[4/3] overflow-hidden rounded-2xl border border-border shadow-lg">
                                <img src={TEAM_IMG} alt="Equipa NOVIFOOD" className="h-full w-full object-cover" />
                            </div>
                        </div>
                    </div>

                    {/* Inline CTA — equipa → galeria */}
                    <div className="mt-14">
                        <InlineCTA
                            eyebrow={tr("O nosso trabalho", "Our work")}
                            title={tr("Vê 19+ carrinhos em rua com marcas reais.", "See 19+ carts on the streets with real brands.")}
                            to="/galeria"
                            label={tr("Abrir galeria", "Open gallery")}
                            variant="navy"
                            testId="sobre-inline-cta-galeria"
                        />
                    </div>
                </div>
            </section>

            {/* TESTEMUNHOS */}
            <section className="container-x section-y" data-testid="sobre-testemunhos">
                <div className="flex items-end justify-between gap-8 flex-wrap">
                    <div className="max-w-2xl">
                        <span className="eyebrow">{t("testimonials_intro", "eyebrow", tr("Quem já roda NOVIFOOD", "Who's rolling with NOVIFOOD"))}</span>
                        <h2 className="mt-3 display-lg text-primary">
                            {t("testimonials_intro", "title", tr("Histórias reais de empreendedores em Portugal.", "Real stories from entrepreneurs in Portugal."))}
                        </h2>
                    </div>
                    <p className="text-muted-foreground max-w-sm">
                        {tr(
                            "Mais de 300 NOVIFOOD em operação, em 15 cidades. Estes são alguns dos seus protagonistas.",
                            "Over 300 NOVIFOOD in operation, across 15 cities. These are some of their protagonists."
                        )}
                    </p>
                </div>

                {loading ? (
                    <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                            <div key={i} className="h-64 rounded-2xl border border-border bg-white animate-pulse" />
                        ))}
                    </div>
                ) : testimonials.length === 0 ? (
                    <p className="mt-14 text-muted-foreground">{tr("Em breve, mais histórias.", "More stories coming soon.")}</p>
                ) : (
                    <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="testimonials-grid">
                        {testimonials.map((t, i) => (
                            <figure key={t.id} data-testid={`testimonial-card-${i}`}
                                className="rounded-2xl border border-border bg-white p-7 sm:p-8 transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-[0_24px_48px_-16px_rgba(11,37,69,0.18)] hover:border-secondary/30">
                                <Quote className="h-7 w-7 text-secondary" />
                                <blockquote className="mt-4 text-primary/90 leading-relaxed">&ldquo;{t.quote}&rdquo;</blockquote>
                                <figcaption className="mt-6 flex items-center gap-4 border-t border-border pt-5">
                                    {t.photo_url ? (
                                        <img src={t.photo_url} alt={t.name} className="h-12 w-12 rounded-full object-cover ring-2 ring-secondary/40" loading="lazy" />
                                    ) : (
                                        <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center font-heading font-bold text-primary">
                                            {t.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                                        </div>
                                    )}
                                    <div>
                                        <div className="font-heading font-bold text-primary">{t.name}</div>
                                        <div className="text-xs text-muted-foreground">{t.role}</div>
                                    </div>
                                </figcaption>
                            </figure>
                        ))}
                    </div>
                )}
            </section>

            {/* CTA FINAL */}
            <section className="container-x pb-20" data-testid="sobre-final-cta">
                <CtaBanner
                    eyebrow={tr("Vamos conversar", "Let's talk")}
                    title={tr("Vamos construir o seu próximo capítulo?", "Shall we build your next chapter?")}
                    description={tr(
                        "A nossa equipa adora projectos com personalidade. Conte-nos a sua ideia e desenhamos uma solução à medida — em 48h tem orçamento na caixa de entrada.",
                        "Our team loves projects with personality. Tell us your idea and we'll design a tailored solution — you'll have a quote in your inbox within 48h."
                    )}
                    primary={{ to: "/contacto", label: tr("Falar com a equipa", "Talk to the team") }}
                    secondary={{ to: `https://wa.me/${whatsappNumber}`, label: tr("Falar no WhatsApp", "Chat on WhatsApp"), external: true, icon: MessageCircle }}
                    variant="yellow"
                    testId="sobre-cta-banner"
                />
            </section>
        </>
    );
}

function MissionTile({ icon: Icon, title, desc }) {
    return (
        <div className="rounded-xl border border-white/10 bg-white/5 p-5 backdrop-blur transition hover:bg-white/10">
            <Icon className="h-6 w-6 text-secondary" />
            <h4 className="mt-4 font-heading text-lg font-bold">{title}</h4>
            <p className="mt-1 text-sm text-white/70 leading-relaxed">{desc}</p>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="rounded-xl border border-border bg-white p-4">
            <div className="font-heading text-3xl font-extrabold text-primary">{value}</div>
            <div className="mt-1 text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">{label}</div>
        </div>
    );
}
